/**
 * Owner: Masa Bayu
 * File: admin-labels.ts
 * Date:Time: 2026-10-23 09:25
 * Description: §467-D — label papan pemuka pentadbir, en + ms (pola locale §381
 *   sama seperti dashboard author). Ruling Owner §467d-R terkunci di sini:
 *   tiada label tindakan tulis (tiada "Set price", "Approve", "Refund") — hanya
 *   salinan pemantauan, dan satu nota yang menjelaskan HAKAPA tiada input harga.
 */

import type { MarketplaceLanguage } from '../home/marketplace/marketplace-locale';

export type AdminNavId = 'overview' | 'publications' | 'orders' | 'accounts' | 'system';

export interface AdminLabels {
  readonly brand: string;
  readonly subtitle: string;
  readonly readOnlyNote: string;
  readonly navAria: string;
  readonly nav: Readonly<Record<AdminNavId, string>>;
  readonly refresh: string;
  readonly generatedAt: string;
  readonly actor: string;
  readonly actorToken: string;
  readonly noData: string;
  readonly none: string;

  // Gerbang akses
  readonly gateSignedOutTitle: string;
  readonly gateSignedOutBody: string;
  readonly gateLoading: string;
  readonly gateForbiddenTitle: string;
  readonly gateForbiddenBody: string;
  readonly gateErrorTitle: string;
  readonly loginCta: string;
  readonly backHome: string;
  readonly retry: string;

  // Ringkasan
  readonly overviewTitle: string;
  readonly overviewBody: string;
  readonly kpiPublications: string;
  readonly kpiPriced: string;
  readonly kpiAccounts: string;
  readonly kpiPaidOrders: string;
  readonly kpiPendingOrders: string;
  readonly kpiRevenue: string;
  readonly kpiActiveGrants: string;
  readonly kpiSessions: string;
  readonly kpiArtifacts: string;
  readonly kpiTakedowns: string;
  readonly revenuePerCurrency: string;
  readonly monthlyTitle: string;
  readonly monthlyAria: string;
  readonly monthlyEmpty: string;
  readonly unownedTitle: string;
  readonly unownedBody: string;
  readonly recentOrdersTitle: string;
  readonly seeAllOrders: string;
  readonly showAll: string;
  readonly showFewer: string;
  readonly roleBreakdown: string;
  readonly roleReader: string;
  readonly rolePublisher: string;
  readonly roleAdmin: string;

  // Terbitan
  readonly publicationsTitle: string;
  readonly publicationsBody: string;
  readonly colTitle: string;
  readonly colId: string;
  readonly colOwner: string;
  readonly colRevision: string;
  readonly colPrice: string;
  readonly colCategory: string;
  readonly colSales: string;
  readonly colBuyers: string;
  readonly colRevenue: string;
  readonly colArtifact: string;
  readonly colPublished: string;
  readonly artifactPresent: string;
  readonly artifactMissing: string;
  readonly unownedBadge: string;
  readonly unpricedBadge: string;
  readonly priceNote: string;
  readonly publicationsEmpty: string;
  readonly searchLabel: string;
  readonly searchPlaceholder: string;
  readonly matchCount: string;
  readonly statePaid: string;
  readonly statePending: string;
  readonly stateFailed: string;
  readonly stateRefunded: string;
  readonly stateUnknown: string;

  // Order
  readonly ordersTitle: string;
  readonly ordersBody: string;
  readonly filterState: string;
  readonly filterPublication: string;
  readonly filterAccount: string;
  readonly filterOrderId: string;
  readonly filterApply: string;
  readonly filterReset: string;
  readonly colOrder: string;
  readonly colAmount: string;
  readonly colState: string;
  readonly colCreated: string;
  readonly colPaid: string;
  readonly colBuyer: string;
  readonly showing: string;
  readonly of: string;
  readonly pagePrev: string;
  readonly pageNext: string;
  readonly ordersEmpty: string;
  readonly loadingOrders: string;
  readonly ordersError: string;
  readonly stateCountsAria: string;

  // Akaun
  readonly accountsTitle: string;
  readonly accountsBody: string;
  readonly colEmail: string;
  readonly colRole: string;
  readonly colBooks: string;
  readonly colGrants: string;
  readonly colOrders: string;
  readonly colSince: string;
  readonly colSignIn: string;
  readonly signInPassword: string;
  readonly signInGoogle: string;
  readonly signInNone: string;
  readonly accountsEmpty: string;
  readonly accountSearchLabel: string;
  readonly roleNote: string;

  // Sistem
  readonly systemTitle: string;
  readonly systemBody: string;
  readonly catalogVersion: string;
  readonly catalogUpdated: string;
  readonly entitlementFiles: string;
  readonly artifactRefs: string;
  readonly takedownRefs: string;
  readonly refsEmpty: string;
  readonly sessionTotal: string;
  readonly sessionActive: string;
}

const EN: AdminLabels = {
  brand: 'QX Admin',
  subtitle: 'Platform monitoring',
  readOnlyNote:
    'Monitoring only. Prices are set when a publisher Publishes — the admin has no write path.',
  navAria: 'Admin sections',
  nav: {
    overview: 'Overview',
    publications: 'Publications',
    orders: 'Orders',
    accounts: 'Accounts',
    system: 'System',
  },
  refresh: 'Refresh',
  generatedAt: 'Snapshot',
  actor: 'Signed in as',
  actorToken: 'Operations token',
  noData: 'No data yet',
  none: 'None',

  gateSignedOutTitle: 'Admin sign-in required',
  gateSignedOutBody:
    'This console shows live platform figures and is only readable by an admin session.',
  gateLoading: 'Checking the admin session…',
  gateForbiddenTitle: 'Not an admin account',
  gateForbiddenBody:
    'Your session is valid but does not carry the admin role. The console stays closed rather than showing invented numbers.',
  gateErrorTitle: 'Console unavailable',
  loginCta: 'Sign in as administrator',
  backHome: 'Back to store',
  retry: 'Try again',

  overviewTitle: 'Platform overview',
  overviewBody: 'Every figure below is read from the hub store at request time.',
  kpiPublications: 'Publications',
  kpiPriced: 'Priced',
  kpiAccounts: 'Accounts',
  kpiPaidOrders: 'Paid orders',
  kpiPendingOrders: 'Awaiting payment',
  kpiRevenue: 'Revenue',
  kpiActiveGrants: 'Active entitlements',
  kpiSessions: 'Live sessions',
  kpiArtifacts: 'Stored artifacts',
  kpiTakedowns: 'Takedowns',
  revenuePerCurrency: 'Revenue is never summed across currencies',
  monthlyTitle: 'Paid revenue by month',
  monthlyAria: 'Monthly orders; per-currency revenue is in each column',
  monthlyEmpty: 'No paid order has been recorded yet.',
  unownedTitle: 'Publications without an owner',
  unownedBody:
    'Claiming an owner needs an operations token (`QXP_HUB_TOKEN`); this console lists them and takes no action.',
  recentOrdersTitle: 'Latest orders',
  seeAllOrders: 'View all orders',
  showAll: 'Show all',
  showFewer: 'Show fewer',
  roleBreakdown: 'Accounts by role',
  roleReader: 'Readers',
  rolePublisher: 'Publishers',
  roleAdmin: 'Admins',

  publicationsTitle: 'Catalog',
  publicationsBody: 'One row per published edition, with its live sales from paid orders.',
  colTitle: 'Title',
  colId: 'Publication ID',
  colOwner: 'Owner',
  colRevision: 'Rev',
  colPrice: 'Price',
  colCategory: 'Category',
  colSales: 'Orders',
  colBuyers: 'Buyers',
  colRevenue: 'Revenue',
  colArtifact: 'Artifact',
  colPublished: 'Published',
  artifactPresent: 'Stored',
  artifactMissing: 'Missing',
  unownedBadge: 'No owner',
  unpricedBadge: 'No price',
  priceNote: 'Price shown for audit only — it is written by the publisher at Publish time.',
  publicationsEmpty: 'The hub catalog is empty.',
  searchLabel: 'Filter by title',
  searchPlaceholder: 'Type to narrow the list',
  matchCount: 'matches',
  statePaid: 'Paid',
  statePending: 'Awaiting payment',
  stateFailed: 'Failed',
  stateRefunded: 'Refunded',
  stateUnknown: 'Unknown',

  ordersTitle: 'Orders',
  ordersBody: 'Full ledger, filtered and paginated at the hub. Nothing here can change an order.',
  filterState: 'State',
  filterPublication: 'Publication ID',
  filterAccount: 'Account ID',
  filterOrderId: 'Order ID',
  filterApply: 'Apply',
  filterReset: 'Clear filters',
  colOrder: 'Order',
  colAmount: 'Amount',
  colState: 'State',
  colCreated: 'Created',
  colPaid: 'Paid',
  colBuyer: 'Buyer',
  showing: 'Showing',
  of: 'of',
  pagePrev: 'Previous',
  pageNext: 'Next',
  ordersEmpty: 'No order matches these filters.',
  loadingOrders: 'Loading orders…',
  ordersError: 'The order ledger could not be read',
  stateCountsAria: 'Order counts by state for the current filter',

  accountsTitle: 'Accounts',
  accountsBody: 'Identity rows from the hub account store.',
  colEmail: 'Email',
  colRole: 'Role',
  colBooks: 'Books',
  colGrants: 'Entitlements',
  colOrders: 'Orders',
  colSince: 'Joined',
  colSignIn: 'Sign-in',
  signInPassword: 'Password',
  signInGoogle: 'Google',
  signInNone: 'No credential',
  accountsEmpty: 'No account is registered yet.',
  accountSearchLabel: 'Filter by email or account ID',
  roleNote:
    'Changing a role is an operations-token action (`POST /v1/admin/accounts/:id/role`); this console does not expose it.',

  systemTitle: 'System',
  systemBody: 'Storage-level counters and references from the hub data directory.',
  catalogVersion: 'Catalog version',
  catalogUpdated: 'Catalog updated',
  entitlementFiles: 'Entitlement files',
  artifactRefs: 'Artifact references',
  takedownRefs: 'Takedown references',
  refsEmpty: 'Nothing stored.',
  sessionTotal: 'Sessions issued',
  sessionActive: 'Sessions not expired',
};

const MS: AdminLabels = {
  brand: 'QX Admin',
  subtitle: 'Pemantauan platform',
  readOnlyNote:
    'Hanya memantau. Harga ditetapkan semasa penerbit Publish — papan pemuka ini tiada laluan tulis.',
  navAria: 'Seksyen admin',
  nav: {
    overview: 'Ringkasan',
    publications: 'Terbitan',
    orders: 'Pesanan',
    accounts: 'Akaun',
    system: 'Sistem',
  },
  refresh: 'Muat semula',
  generatedAt: 'Snapshot',
  actor: 'Log masuk sebagai',
  actorToken: 'Token operasi',
  noData: 'Tiada data lagi',
  none: 'Tiada',

  gateSignedOutTitle: 'Perlu log masuk pentadbir',
  gateSignedOutBody:
    'Konsol ini memaparkan angka platform yang sebenar dan hanya boleh dibaca oleh sesi pentadbir.',
  gateLoading: 'Menyemak sesi pentadbir…',
  gateForbiddenTitle: 'Bukan akaun pentadbir',
  gateForbiddenBody:
    'Sesi anda sah tetapi tidak membawa peranan pentadbir. Konsol kekal tertutup dan tidak memaparkan angka rekaan.',
  gateErrorTitle: 'Konsol tidak tersedia',
  loginCta: 'Log masuk sebagai pentadbir',
  backHome: 'Kembali ke kedai',
  retry: 'Cuba lagi',

  overviewTitle: 'Ringkasan platform',
  overviewBody: 'Setiap angka di bawah dibaca daripada stor hub pada saat permintaan.',
  kpiPublications: 'Terbitan',
  kpiPriced: 'Ada harga',
  kpiAccounts: 'Akaun',
  kpiPaidOrders: 'Pesanan berbayar',
  kpiPendingOrders: 'Menunggu bayaran',
  kpiRevenue: 'Hasil',
  kpiActiveGrants: 'Geran aktif',
  kpiSessions: 'Sesi hidup',
  kpiArtifacts: 'Artifak tersimpan',
  kpiTakedowns: 'Takedown',
  revenuePerCurrency: 'Hasil tidak dijumlah rentas mata wang',
  monthlyTitle: 'Hasil berbayar mengikut bulan',
  monthlyAria: 'Pesanan bulanan; hasil setiap mata wang ada dalam lajur',
  monthlyEmpty: 'Belum ada order berbayar direkodkan.',
  unownedTitle: 'Terbitan tanpa pemilik',
  unownedBody:
    'Klaim pemilik memerlukan token operasi (`QXP_HUB_TOKEN`); konsol ini hanya menyenaraikannya dan tidak bertindak.',
  recentOrdersTitle: 'Pesanan terkini',
  seeAllOrders: 'Lihat semua pesanan',
  showAll: 'Tunjuk semua',
  showFewer: 'Tunjuk ringkas',
  roleBreakdown: 'Akaun ikut peranan',
  roleReader: 'Pembaca',
  rolePublisher: 'Penerbit',
  roleAdmin: 'Pentadbir',

  publicationsTitle: 'Katalog',
  publicationsBody:
    'Satu baris bagi setiap edisi yang disiarkan, bersama jualan hidup daripada order berbayar.',
  colTitle: 'Tajuk',
  colId: 'ID terbitan',
  colOwner: 'Pemilik',
  colRevision: 'Rev',
  colPrice: 'Harga',
  colCategory: 'Kategori',
  colSales: 'Pesanan',
  colBuyers: 'Pembeli',
  colRevenue: 'Hasil',
  colArtifact: 'Artifak',
  colPublished: 'Disiarkan',
  artifactPresent: 'Tersimpan',
  artifactMissing: 'Tiada',
  unownedBadge: 'Tanpa pemilik',
  unpricedBadge: 'Tanpa harga',
  priceNote: 'Harga dipapar untuk audit sahaja — ia ditulis penerbit semasa Publish.',
  publicationsEmpty: 'Katalog hub kosong.',
  searchLabel: 'Tapis ikut tajuk',
  searchPlaceholder: 'Taip untuk memperkecil senarai',
  matchCount: 'sepadan',
  statePaid: 'Berbayar',
  statePending: 'Menunggu bayaran',
  stateFailed: 'Gagal',
  stateRefunded: 'Dibayar balik',
  stateUnknown: 'Tidak diketahui',

  ordersTitle: 'Pesanan',
  ordersBody:
    'Ledger penuh, ditapis dan dipaginasi di hub. Tiada apa di sini boleh mengubah pesanan.',
  filterState: 'Keadaan',
  filterPublication: 'ID terbitan',
  filterAccount: 'ID akaun',
  filterOrderId: 'ID order',
  filterApply: 'Tapis',
  filterReset: 'Kosongkan penapis',
  colOrder: 'Pesanan',
  colAmount: 'Jumlah',
  colState: 'Keadaan',
  colCreated: 'Dicipta',
  colPaid: 'Dibayar',
  colBuyer: 'Pembeli',
  showing: 'Menunjukkan',
  of: 'daripada',
  pagePrev: 'Sebelumnya',
  pageNext: 'Seterusnya',
  ordersEmpty: 'Tiada pesanan yang sepadan dengan penapis ini.',
  loadingOrders: 'Memuatkan pesanan…',
  ordersError: 'Ledger pesanan tidak dapat dibaca',
  stateCountsAria: 'Kiraan pesanan ikut keadaan untuk penapis semasa',

  accountsTitle: 'Akaun',
  accountsBody: 'Baris identiti daripada stor akaun hub.',
  colEmail: 'E-mel',
  colRole: 'Peranan',
  colBooks: 'Buku',
  colGrants: 'Geran',
  colOrders: 'Pesanan',
  colSince: 'Sertai',
  colSignIn: 'Log masuk',
  signInPassword: 'Kata laluan',
  signInGoogle: 'Google',
  signInNone: 'Tiada kredential',
  accountsEmpty: 'Belum ada akaun didaftarkan.',
  accountSearchLabel: 'Tapis ikut e-mel atau ID akaun',
  roleNote:
    'Menukar peranan ialah tindakan token operasi (`POST /v1/admin/accounts/:id/role`); konsol ini tidak mendedahkannya.',

  systemTitle: 'Sistem',
  systemBody: 'Kiraan peringkat stor dan rujukan daripada direktori data hub.',
  catalogVersion: 'Versi katalog',
  catalogUpdated: 'Katalog dikemas kini',
  entitlementFiles: 'Fail geran',
  artifactRefs: 'Rujukan artifak',
  takedownRefs: 'Rujukan takedown',
  refsEmpty: 'Tiada apa tersimpan.',
  sessionTotal: 'Sesi dikeluarkan',
  sessionActive: 'Sesi belum tamat',
};

export const ADMIN_LABELS: Readonly<Record<MarketplaceLanguage, AdminLabels>> = Object.freeze({
  en: Object.freeze(EN),
  ms: Object.freeze(MS),
});
